import { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart, Bar,
  AreaChart, Area,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";
const authFetch = url => fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// ── Design system ────────────────────────────────────────────────────────────
const DS = {
  green:  "#059669", greenBg: "#ECFDF5", greenBorder: "rgba(5,150,105,0.15)",
  indigo: "#4338CA", indigoBg: "#EEF2FF",
  amber:  "#D97706", amberBg: "#FFFBEB",
  red:    "#DC2626", redBg: "#FEF2F2",
  lbl:    "#9496B4",
  border: "#E4E6F5",
  mono:   "'JetBrains Mono', monospace",
};

const COLORS_PRODUCTOS = [
  "#059669","#4338CA","#D97706","#0891B2","#7C3AED","#DB2777","#EA580C","#65A30D",
];

// ── Formatters ───────────────────────────────────────────────────────────────
const fmtPesos  = n => {
  const v = Math.round(n ?? 0);
  if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(1) + "M";
  if (Math.abs(v) >= 1_000)     return "$" + (v / 1_000).toFixed(0) + "k";
  return "$" + v.toLocaleString("es-CL");
};
const fmtFecha = s => {
  if (!s) return "";
  const [, m, d] = s.split("-");
  return `${d}/${m}`;
};

// ── Tooltip personalizado ────────────────────────────────────────────────────
function TooltipCustom({ active, payload, label, moneda }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#1E1E2E", borderRadius: 10, padding: "10px 14px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.25)", minWidth: 140,
    }}>
      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, fontSize: 12, color: "#fff", fontWeight: 600 }}>
          <span style={{ color: p.color || "#aaa", fontWeight: 400 }}>{p.name}</span>
          <span style={{ fontFamily: DS.mono }}>{moneda ? fmtPesos(p.value) : p.value?.toLocaleString("es-CL")}</span>
        </div>
      ))}
    </div>
  );
}

// ── Contenedor de card de gráfico ────────────────────────────────────────────
function ChartCard({ titulo, sub, children, loading }) {
  return (
    <div style={{
      background: "#fff", border: `1px solid ${DS.border}`, borderRadius: 16,
      padding: "20px 20px 14px", display: "flex", flexDirection: "column", gap: 12,
      minHeight: 280,
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", letterSpacing: "-0.02em" }}>{titulo}</div>
        {sub && <div style={{ fontSize: 11, color: DS.lbl, marginTop: 3 }}>{sub}</div>}
      </div>
      {loading
        ? <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 20, height: 20, border: `2px solid ${DS.border}`,
              borderTop: `2px solid ${DS.indigo}`, borderRadius: "50%",
              animation: "spin 0.8s linear infinite" }} />
          </div>
        : <div style={{ flex: 1 }}>{children}</div>
      }
    </div>
  );
}

// ── Gráfico 1: Pedidos por día ───────────────────────────────────────────────
function GrafPedidosDia({ datos }) {
  if (!datos.length) return <div style={{ color: DS.lbl, fontSize: 13 }}>Sin datos para el período.</div>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={datos} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <defs>
          <linearGradient id="gradPedidos" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={DS.indigo} stopOpacity={0.18} />
            <stop offset="95%" stopColor={DS.indigo} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={DS.border} vertical={false} />
        <XAxis dataKey="fecha" tickFormatter={fmtFecha} tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip content={<TooltipCustom />} />
        <Area type="monotone" dataKey="total_pedidos" name="Pedidos" stroke={DS.indigo} strokeWidth={2} fill="url(#gradPedidos)" dot={false} activeDot={{ r: 4, fill: DS.indigo }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Gráfico 2: Productos más vendidos ───────────────────────────────────────
function GrafProductos({ productos, loading }) {
  if (loading) return null;
  if (!productos.length) return <div style={{ color: DS.lbl, fontSize: 13 }}>Sin datos para el período.</div>;

  const data = productos
    .sort((a, b) => b.total_pedidos - a.total_pedidos)
    .slice(0, 8)
    .map(p => ({
      nombre: p.nombre?.length > 22 ? p.nombre.slice(0, 20) + "…" : p.nombre,
      pedidos: p.total_pedidos,
      entregados: p.entregados ?? 0,
    }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DS.border} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} allowDecimals={false} />
        <YAxis type="category" dataKey="nombre" tick={{ fontSize: 10, fill: "#374151" }} axisLine={false} tickLine={false} width={100} />
        <Tooltip content={<TooltipCustom />} />
        <Bar dataKey="pedidos" name="Pedidos" radius={[0, 4, 4, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS_PRODUCTOS[i % COLORS_PRODUCTOS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Gráfico 3: Margen real por día ───────────────────────────────────────────
// Barras color por signo: verde si positivo, rojo si negativo. Más claro
// que la línea continua para detectar días en pérdida de un vistazo.
function GrafGanancias({ datos }) {
  if (!datos.length) return <div style={{ color: DS.lbl, fontSize: 13 }}>Sin datos para el período.</div>;
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={datos} margin={{ top: 4, right: 8, left: -4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DS.border} vertical={false} />
        <XAxis dataKey="fecha" tickFormatter={fmtFecha} tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tickFormatter={fmtPesos} tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} />
        <Tooltip content={<TooltipCustom moneda />} />
        <Bar dataKey="margen_real_actual" name="Margen real" radius={[3, 3, 0, 0]}>
          {datos.map((d, i) => (
            <Cell key={i} fill={(d.margen_real_actual ?? 0) >= 0 ? DS.green : DS.red} fillOpacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Gráfico 4: Fletes fallidos por día ───────────────────────────────────────
function GrafFallidosDia({ datos }) {
  const conFallidos = datos.filter(d => d.fallidos > 0);
  if (!conFallidos.length) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      height: 200, gap: 8 }}>
      <div style={{ fontSize: 28 }}>✅</div>
      <div style={{ fontSize: 13, color: DS.green, fontWeight: 600 }}>Sin fletes fallidos en el período</div>
    </div>
  );
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={datos} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={DS.border} vertical={false} />
        <XAxis dataKey="fecha" tickFormatter={fmtFecha} tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10, fill: DS.lbl }} axisLine={false} tickLine={false} allowDecimals={false} />
        <Tooltip content={<TooltipCustom />} />
        <Bar dataKey="fallidos" name="Fallidos" fill={DS.red} radius={[4, 4, 0, 0]}>
          {datos.map((entry, i) => (
            <Cell key={i} fill={entry.fallidos > 0 ? DS.red : DS.border} fillOpacity={entry.fallidos > 0 ? 0.85 : 0.3} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── KPI resumen rápido ───────────────────────────────────────────────────────
function KpiResumen({ datos, productos, margenReal }) {
  const totalPedidos  = datos.reduce((s, d) => s + (d.total_pedidos ?? 0), 0);
  const totalFallidos = datos.reduce((s, d) => s + (d.fallidos ?? 0), 0);
  const topProducto   = productos.length
    ? productos.sort((a, b) => b.total_pedidos - a.total_pedidos)[0]?.nombre
    : "—";

  const kpis = [
    { label: "Total pedidos", value: totalPedidos.toLocaleString("es-CL"), color: DS.indigo, bg: DS.indigoBg },
    { label: "Margen Real (Pulso)", value: margenReal != null ? fmtPesos(margenReal) : "—", color: DS.green, bg: DS.greenBg },
    { label: "Fletes fallidos", value: totalFallidos.toLocaleString("es-CL"), color: totalFallidos > 0 ? DS.red : DS.green, bg: totalFallidos > 0 ? DS.redBg : DS.greenBg },
    { label: "Producto líder", value: topProducto?.length > 18 ? topProducto.slice(0, 16) + "…" : (topProducto ?? "—"), color: DS.amber, bg: DS.amberBg, small: true },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 4 }}>
      {kpis.map(k => (
        <div key={k.label} style={{
          background: k.bg, border: `1px solid ${k.color}20`,
          borderRadius: 12, padding: "14px 16px",
          borderLeft: `3px solid ${k.color}`,
        }}>
          <div style={{ fontSize: 10, color: DS.lbl, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 6 }}>
            {k.label}
          </div>
          <div style={{ fontFamily: DS.mono, fontSize: k.small ? 14 : 20, fontWeight: 700, color: k.color, lineHeight: 1.2 }}>
            {k.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export default function Dashboard({ diario, desde, hasta }) {
  const [productos, setProductos]       = useState([]);
  const [loadingProd, setLoadingProd]   = useState(false);
  const [pulsoData, setPulsoData]       = useState(null);

  // Filtrar diario por período
  const datosFiltrados = (diario || []).filter(d => {
    if (!d.fecha) return false;
    return d.fecha >= desde && d.fecha <= hasta;
  });

  // Fetch de ambas fuentes al cambiar período
  const fetchDatos = useCallback(async (d, h) => {
    if (!d || !h) return;
    setLoadingProd(true);
    try {
      const [resProd, resPulso] = await Promise.all([
        authFetch(`${API_URL}/api/productos-detalle?desde=${d}&hasta=${h}`),
        authFetch(`${API_URL}/api/pulso?desde=${d}&hasta=${h}`),
      ]);
      if (resProd.ok)  setProductos(await resProd.json());
      if (resPulso.ok) setPulsoData(await resPulso.json());
    } finally {
      setLoadingProd(false);
    }
  }, []);

  useEffect(() => { fetchDatos(desde, hasta); }, [desde, hasta, fetchDatos]);

  // Margen Real — idéntico al cálculo de Pulso.jsx
  const margenReal = pulsoData
    ? (pulsoData.venta_total ?? 0)
      - (pulsoData.flete_entregados   ?? 0)
      - (pulsoData.costo_proveedor    ?? 0)
      - (pulsoData.gasto_meta         ?? 0)
      - (pulsoData.flete_devoluciones ?? 0)
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* KPIs resumen */}
      <KpiResumen datos={datosFiltrados} productos={productos} margenReal={margenReal} />

      {/* Grid 2×2 de gráficos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(clamp(280px, 45%, 560px), 1fr))", gap: 16 }}>

        <ChartCard
          titulo="Pedidos por día"
          sub={`${desde} → ${hasta} · total: ${datosFiltrados.reduce((s,d) => s + (d.total_pedidos ?? 0), 0).toLocaleString("es-CL")} pedidos`}
        >
          <GrafPedidosDia datos={datosFiltrados} />
        </ChartCard>

        <ChartCard
          titulo="Productos más vendidos"
          sub="Top 8 por cantidad de pedidos en el período"
          loading={loadingProd}
        >
          <GrafProductos productos={productos} loading={loadingProd} />
        </ChartCard>

        <ChartCard
          titulo="Margen real por día"
          sub="Venta − flete − proveedor − Meta Ads − fletes fallidos (igual que Pulso)"
        >
          <GrafGanancias datos={datosFiltrados} />
        </ChartCard>

        <ChartCard
          titulo="Fletes fallidos por día"
          sub="Pedidos devueltos o rechazados por día"
        >
          <GrafFallidosDia datos={datosFiltrados} />
        </ChartCard>

      </div>
    </div>
  );
}
